import React, { createContext, useContext, useState, useEffect, useMemo } from 'react';
import { createTheme, ThemeProvider as MuiThemeProvider, ThemeOptions } from '@mui/material/styles';
import { CssBaseline } from '@mui/material';
import { useDebouncedLocalStorage } from '../hooks/useDebouncedLocalStorage';
import { getFontFamily } from '../config/fonts';

interface ThemeContextType {
  isDarkMode: boolean;
  toggleTheme: () => void;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

const lightTheme: ThemeOptions = {
  palette: {
    mode: 'light',
    primary: {
      main: '#7D7AFF',
    },
    background: {
      default: '#FFFFFF',
      paper: '#FFFFFF',
    },
    text: {
      primary: '#000000',
      secondary: 'rgba(0, 0, 0, 0.6)',
    },
  },
  typography: {
    fontFamily: getFontFamily(),
    h7: {
      fontSize: { xs: '1rem', sm: '1.125rem' } as any, // 16px on mobile, 18px on desktop
      fontWeight: 600,
      lineHeight: 1.33,
      fontFamily: getFontFamily(),
    } as any,
  },
  components: {
    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundColor: '#FFFFFF',
          color: '#000000',
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundColor: '#FFFFFF',
        },
      },
    },
    MuiBadge: {
      styleOverrides: {
        badge: {
          boxShadow: '0 0 0 2px #FFFFFF',
        },
      },
    },
    // iOS WKWebView auto-zooms when an input has font-size < 16px.
    // Setting a global minimum of 16px on all inputs prevents this.
    MuiInputBase: {
      styleOverrides: {
        input: {
          fontSize: '16px !important',
        },
      },
    },
  },
};

const darkTheme: ThemeOptions = {
  palette: {
    mode: 'dark',
    primary: {
      main: '#7D7AFF',
    },
    background: {
      default: '#121212',
      paper: '#2C2C2E',
    },
    text: {
      primary: 'rgba(255, 255, 255, 0.85)',
      secondary: 'rgba(255, 255, 255, 0.6)',
    },
  },
  typography: {
    fontFamily: getFontFamily(),
    h7: {
      fontSize: { xs: '1rem', sm: '1.125rem' } as any, // 16px on mobile, 18px on desktop
      fontWeight: 700,
      lineHeight: 1.33,
      fontFamily: getFontFamily(),
    } as any,
  },
  components: {
    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundColor: '#1C1C1E',
          color: '#FFFFFF',
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundColor: '#2C2C2E',
        },
      },
    },
    MuiDialogContent: {
      styleOverrides: {
        root: {
          overflowX: 'hidden',
        },
      },
    },
    MuiIconButton: {
      styleOverrides: {
        root: {
          color: '#FFFFFF',
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          color: '#FFFFFF',
        },
      },
    },
    MuiBadge: {
      styleOverrides: {
        badge: {
          boxShadow: '0 0 0 2px #1C1C1E',
        },
      },
    },
    // iOS WKWebView auto-zooms when an input has font-size < 16px.
    // Setting a global minimum of 16px on all inputs prevents this.
    MuiInputBase: {
      styleOverrides: {
        input: {
          fontSize: '16px !important',
        },
      },
    },
  },
};

interface ThemeProviderProps {
  children: React.ReactNode;
}

export const ThemeProvider: React.FC<ThemeProviderProps> = ({ children }) => {
  const { setItem: setDebouncedItem } = useDebouncedLocalStorage(1000);
  const [isDarkMode, setIsDarkMode] = useState(() => {
    // Force dark mode for now - disable light mode
    return true;
  });

  useEffect(() => {
    setDebouncedItem('darkMode', JSON.stringify(isDarkMode));
  }, [isDarkMode, setDebouncedItem]);

  // Update theme-color meta tag and status bar CSS variable dynamically based on current theme
  useEffect(() => {
    const metaThemeColor = document.querySelector('meta[name="theme-color"]');
    const backgroundColor = isDarkMode ? '#1C1C1E' : '#FFFFFF';

    if (metaThemeColor) {
      metaThemeColor.setAttribute('content', backgroundColor);
    }

    // Update CSS variable for status bar background (used by body::before pseudo-element)
    document.documentElement.style.setProperty('--status-bar-color', backgroundColor);
  }, [isDarkMode]);

  const toggleTheme = () => {
    // Disable theme toggle for now - keep dark mode only
    console.log('Light mode is temporarily disabled');
    // setIsDarkMode(!isDarkMode);
  };

  const theme = useMemo(() =>
    createTheme(isDarkMode ? darkTheme : lightTheme),
    [isDarkMode]
  );

  return (
    <ThemeContext.Provider value={{ isDarkMode, toggleTheme }}>
      <MuiThemeProvider theme={theme}>
        <CssBaseline />
        {children}
      </MuiThemeProvider>
    </ThemeContext.Provider>
  );
}; 